import { ref, onUnmounted } from 'vue';

export interface MediaItem {
    file?: File;
    url?: string;
    media_id?: string;
    filename?: string;
    metadata?: Record<string, unknown>;
    uploadedAt?: Date | string;
    isTemporary?: boolean;
}

export interface MediaUploadResult {
    id: string;
    url: string;
    filename?: string;
    metadata?: Record<string, unknown>;
}

interface TrackedMedia {
    media_id: string;
    url: string;
    uploadedAt: Date;
    isTemporary: boolean;
    originalFile: File;
}

export interface UseMediaUploadOptions {
    uploadFn?: (file: File, collection: string, metadata?: object) => Promise<MediaUploadResult>;
    deleteFn?: (item: MediaItem) => Promise<void>;
    collection?: string;
    metadata?: object;
    refreshInterval?: number; // in milliseconds
}

export function useMediaUpload(options: UseMediaUploadOptions = {}) {
    const {
        uploadFn,
        deleteFn,
        collection = 'default',
        metadata = {},
        refreshInterval = 86400000, // 24 hours default
    } = options;

    const trackedMedia = ref<Map<string, TrackedMedia>>(new Map());
    let expirationIntervalId: number | undefined;

    async function uploadFile(file: File): Promise<MediaUploadResult | null> {
        if (!uploadFn) {
            console.error('[useMediaUpload] uploadFn is not provided');
            return null;
        }

        try {
            const result = await uploadFn(file, collection, metadata);
            return result;
        } catch (error) {
            console.error('[useMediaUpload] Upload failed:', error);
            return null;
        }
    }

    async function uploadMultiple(files: File[]): Promise<Array<MediaUploadResult | null>> {
        return Promise.all(files.map(file => uploadFile(file)));
    }

    async function deleteMedia(item: MediaItem): Promise<boolean> {
        if (!deleteFn) {
            console.error('[useMediaUpload] deleteFn is not provided');
            return false;
        }

        try {
            await deleteFn(item);
            if (item.media_id) {
                trackedMedia.value.delete(item.media_id);
            }
            return true;
        } catch (error) {
            console.error('[useMediaUpload] Delete failed:', error);
            return false;
        }
    }

    async function handleFileSelection(
        files: File[],
        onUpdate: (items: MediaItem[]) => void
    ): Promise<void> {
        if (!uploadFn || files.length === 0) {
            return;
        }

        let results: Array<MediaUploadResult | null>;
        
        if (files.length === 1) {
            const result = await uploadFile(files[0]);
            results = [result];
        } else {
            results = await uploadMultiple(files);
        }

        const items: MediaItem[] = [];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const result = results[i];

            if (result) {
                const uploadedAt = new Date();
                const item: MediaItem = {
                    file, // Keep file reference internally for preview
                    url: result.url,
                    media_id: result.id,
                    filename: result.filename,
                    metadata: result.metadata,
                    uploadedAt,
                    isTemporary: true,
                };

                trackedMedia.value.set(result.id, {
                    media_id: result.id,
                    url: result.url,
                    uploadedAt,
                    isTemporary: true,
                    originalFile: file,
                });

                items.push(item);
            } else {
                // Upload failed, keep file for retry
                items.push({ file });
            }
        }

        onUpdate(items);
    }

    async function handleFileRemoval(
        item: MediaItem,
        onUpdate: () => void
    ): Promise<void> {
        if (deleteFn) {
            await deleteMedia(item);
        }

        onUpdate();
    }

    function trackTemporaryMedia(item: MediaItem): void {
        if (!item.media_id || !item.isTemporary || !item.file) {
            return;
        }

        trackedMedia.value.set(item.media_id, {
            media_id: item.media_id,
            url: item.url || '',
            uploadedAt: typeof item.uploadedAt === 'string' 
                ? new Date(item.uploadedAt) 
                : item.uploadedAt || new Date(),
            isTemporary: true,
            originalFile: item.file,
        });
    }

    async function refreshExpiredMedia(
        onUpdate?: (mediaId: string, newMediaId: string, newUrl: string) => void
    ): Promise<void> {
        const now = Date.now();
        const expiredItems: TrackedMedia[] = [];

        for (const [mediaId, tracked] of trackedMedia.value.entries()) {
            const uploadedTime = tracked.uploadedAt.getTime();
            const timeSinceUpload = now - uploadedTime;

            if (timeSinceUpload >= refreshInterval) {
                expiredItems.push(tracked);
            }
        }

        for (const expired of expiredItems) {
            const result = await uploadFile(expired.originalFile);
            
            if (result) {
                trackedMedia.value.delete(expired.media_id);
                
                const uploadedAt = new Date();
                trackedMedia.value.set(result.id, {
                    media_id: result.id,
                    url: result.url,
                    uploadedAt,
                    isTemporary: true,
                    originalFile: expired.originalFile,
                });

                if (onUpdate) {
                    onUpdate(expired.media_id, result.id, result.url);
                }
            }
        }
    }

    function startExpirationCheck(
        interval: number,
        onUpdate?: (mediaId: string, newMediaId: string, newUrl: string) => void
    ): void {
        if (expirationIntervalId !== undefined) {
            stopExpirationCheck();
        }

        expirationIntervalId = window.setInterval(() => {
            refreshExpiredMedia(onUpdate);
        }, interval);
    }

    function stopExpirationCheck(): void {
        if (expirationIntervalId !== undefined) {
            clearInterval(expirationIntervalId);
            expirationIntervalId = undefined;
        }
    }

    onUnmounted(() => {
        stopExpirationCheck();
    });

    return {
        uploadFile,
        uploadMultiple,
        deleteMedia,
        handleFileSelection,
        handleFileRemoval,
        trackTemporaryMedia,
        refreshExpiredMedia,
        startExpirationCheck,
        stopExpirationCheck,
    };
}
